import { SingingPortraitFragment } from 'api/contentful/contentful.graphql.types'
import {
  useContentfulLivePortraits,
  useContentfulSingingPortraits,
  useStoryPortraitCarousel,
} from 'api/contentful/memory'
import { postUserMemory } from 'api/goodtrust/user/memory'
import { AnimatedPortraitBanner } from 'components/content/lifeStories/lifeStoriesBanners/AnimatedPortraitBanner'
import { SLifeStoriesBanners } from './LifeStoriesBanner.styled'
import { OngoingUploadPlaceholder } from 'components/content/lifeStories/lifeStoriesBanners/OngoingUploadPlaceholder'
import { useChooseSongModal } from 'components/content/singingPortraits/chooseSongModal/ChooseSongModal'
import { ImagePickerHidden } from 'components/imagePicker/ImagePickerHidden'
import { useChooseAnimationModal } from 'components/memory/chooseAnimationModal/ChooseAnimationModal'
import { AnimationDriver, AnimationType } from 'components/memory/MemoryUtils'
import { useStoryPortraitLogic } from 'components/storyPortraits/logic'
import { handleAndToastError } from 'components/Toast'
import { useTranslation } from 'next-i18next'
import { ReactElement, useRef, useState } from 'react'
import { fireEvent } from 'utils/analytics'
import { isNonNullable } from 'utils/general'
import { useWindowSize } from 'utils/hooks'

interface OngoingUpload {
  placeholder?: ReactElement
  type?: AnimationType
}

export const NO_OF_VIDEOS = 3

export function LifeStoriesBanners(props: {
  onUpload: (uuid: string, driver: AnimationDriver) => Promise<void>
  className?: string
}) {
  const { t } = useTranslation('life_stories')
  const singingPortraits = useContentfulSingingPortraits().data?.slice(0, NO_OF_VIDEOS)
  const livePortraits = useContentfulLivePortraits().data?.slice(0, NO_OF_VIDEOS)
  const storyPortraits = useStoryPortraitCarousel()
    .data?.memorySetupCollection?.items[0]?.storyPortraitCarouselCollection?.items.filter(
      isNonNullable
    )
    .map((item): SingingPortraitFragment => {
      return {
        sys: item?.sys,
        video: {
          url: item.url,
        },
      }
    })
    .slice(0, NO_OF_VIDEOS)

  const { isDesktop } = useWindowSize()
  const inputRef = useRef<HTMLInputElement>(null)

  const [ongoingUpload, setOngoingUpload] = useState<OngoingUpload | null>(null)

  const [chooseSongModal, chooseSongViaModal] = useChooseSongModal()
  const [chooseAnimationModal, chooseAnimationViaModal] = useChooseAnimationModal()

  const onClick = (type: AnimationType) => {
    setOngoingUpload({ type })
    inputRef.current?.click()
  }

  const storyChooseImageButton = useStoryPortraitLogic().useChooseImageButton()

  const upload = async (file?: File) => {
    try {
      if (!file) throw Error()
      setOngoingUpload((prev) => ({
        ...prev,
        placeholder: <OngoingUploadPlaceholder src={URL.createObjectURL(file)} />,
      }))
      const res = await postUserMemory(file)
      if (!res.ok || !res.json?.uuid) throw Error(res?.error?.toString?.())
      fireEvent('photo_uploaded', { page: 'banner_flow', photo_source: 'file_upload' })
      let driver: AnimationDriver | null
      if (ongoingUpload?.type === 'SINGING_PORTRAIT') {
        driver = await chooseSongViaModal()
        fireEvent('sh_photo_uploaded', { page: 'memories_dashboard', photo_source: 'file_upload' })
      } else {
        driver = await chooseAnimationViaModal()
        fireEvent('photo_uploaded', { page: 'memories_dashboard', photo_source: 'file_upload' })
      }
      if (!driver) {
        return
      }
      await props.onUpload?.(res?.json?.uuid, driver)
    } catch (err) {
      handleAndToastError(err, 'something_went_wrong')
    } finally {
      setOngoingUpload(null)
    }
  }

  return (
    <>
      {chooseSongModal}
      {chooseAnimationModal}
      {storyChooseImageButton.modal}
      <ImagePickerHidden ref={inputRef} onFileSelect={upload} data-testid="upload-photo" />
      <SLifeStoriesBanners className={props.className}>
        <AnimatedPortraitBanner
          title={t('life_stories.inside_page.banners.live_portraits.title')}
          description={
            isDesktop
              ? t('life_stories.inside_page.banners.live_portraits.description_desktop')
              : t('life_stories.inside_page.banners.live_portraits.description_mobile')
          }
          carouselData={livePortraits}
          uploadPlaceholder={ongoingUpload?.type === 'LIVE_PORTRAIT' && ongoingUpload.placeholder}
          onClick={() => onClick('LIVE_PORTRAIT')}
        />
        <AnimatedPortraitBanner
          title={t('life_stories.inside_page.banners.singing_portraits.title')}
          description={
            isDesktop
              ? t('life_stories.inside_page.banners.singing_portraits.description_desktop')
              : t('life_stories.inside_page.banners.singing_portraits.description_mobile')
          }
          carouselData={singingPortraits}
          uploadPlaceholder={
            ongoingUpload?.type === 'SINGING_PORTRAIT' && ongoingUpload.placeholder
          }
          onClick={() => onClick('SINGING_PORTRAIT')}
          showMuteButton
        />
        <AnimatedPortraitBanner
          title={t('life_stories.inside_page.banners.story_portraits.title')}
          description={
            isDesktop
              ? t('life_stories.inside_page.banners.story_portraits.description_desktop')
              : t('life_stories.inside_page.banners.story_portraits.description_mobile')
          }
          carouselData={storyPortraits}
          onClick={storyChooseImageButton.onClick}
          buttonText={t('life_stories.inside_page.banners.story_portraits.button')}
          showMuteButton
        />
      </SLifeStoriesBanners>
    </>
  )
}
